import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@/lib/logger';
import { isModuleLoadError, recovery } from '@/lib/lazy-route';

interface Props {
  /** Render-tree to protect. */
  children: ReactNode;
  /**
   * Optional override for the fallback UI. Receives the caught error
   * and a `reset` callback that clears the boundary so the children
   * re-mount. If omitted, the boundary renders {@link DefaultFallback}.
   */
  fallback?: (props: { error: Error; reset: () => void }) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort guard against render-time exceptions. React 19 still has
 * no hook-based equivalent, so this stays a class component.
 *
 * Async errors (Promise rejection, setTimeout callbacks, network
 * failures) DO NOT trigger this boundary — handle those at the call
 * site (e.g. TanStack Query's `error` state) and use `logger.error`
 * for explicit reporting.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('render error', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback({ error, reset: this.reset });
    return <DefaultFallback error={error} reset={this.reset} />;
  }
}

/**
 * Clearing the boundary re-mounts the children, which is the right recovery
 * for a one-off render throw. It is useless against a chunk that could not
 * load: `React.lazy` caches the rejected promise, so the retry re-throws the
 * identical error and the reader is stuck on this screen for good. That case
 * needs a fresh document — see `lib/lazy-route.ts`.
 */
function retry(error: Error, reset: () => void): void {
  if (isModuleLoadError(error)) {
    recovery.reload();
    return;
  }
  reset();
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }): React.JSX.Element {
  return (
    <main
      role="alert"
      className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-surface p-6 text-center"
    >
      <h1 className="text-xl font-bold text-foreground">Something went wrong</h1>
      <p className="max-w-sm text-sm text-muted">
        We've logged the error. Try again — most issues clear after a refresh.
      </p>
      {import.meta.env.DEV && (
        <pre className="max-w-full overflow-auto rounded-xl bg-card p-3 text-xs text-error">
          {error.message}
        </pre>
      )}
      <button
        type="button"
        onClick={() => retry(error, reset)}
        className="h-12 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground"
      >
        Try again
      </button>
    </main>
  );
}
